import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {LayoutComponent} from './_layout/layout.component';
import {AuthGuard} from '../modules/auth/_services/auth.guard';
import {UserRolesGuard} from '../modules/auth/_services/user-roles.guard';
import {AllNotificationsComponent} from './all-notifications/all-notifications.component';
import {TermsAndConditionsComponent} from './terms-and-conditions/terms-and-conditions.component';
import { AboutUsComponent } from './about-us/about-us.component';

const routes: Routes = [
    {
        path: '',
        component: LayoutComponent,
        children: [
            {
                path: 'dashboard',
                canActivate: [AuthGuard, UserRolesGuard],
                data: {
                    allowedRoles: ['ROLE_super_admin', 'ROLE_admin', 'ROLE_user', 'ROLE_demo_user']
                },
                loadChildren: () =>
                    import('./dashboard/dashboard.module').then((m) => m.DashboardModule),
            },
            {
                path: 'notifications',
                canActivate: [AuthGuard],
                component: AllNotificationsComponent
            },
            {
                path: 'terms-and-conditions',
                canActivate: [AuthGuard],
                component: TermsAndConditionsComponent
            },
            {
                path : 'about-us',
                canActivate: [AuthGuard],
                component: AboutUsComponent
            },
            {
                path: 'products',
                canActivate: [AuthGuard, UserRolesGuard],
                data: {
                    allowedRoles: ['ROLE_super_admin', 'ROLE_admin', 'ROLE_ProductsFull', 'ROLE_ProductsView', 'ROLE_demo_user']
                },
                loadChildren: () =>
                    import('../modules/products/products.module').then(
                        (m) => m.ProductsModule
                    ),
            },
            {
                path: 'days-off',
                canActivate: [AuthGuard, UserRolesGuard],
                data: {
                    allowedRoles: ['ROLE_super_admin', 'ROLE_admin', 'ROLE_user', 'ROLE_demo_user']
                },
                loadChildren: () =>
                    import('../modules/days-off/days-off.module').then(
                        (m) => m.DaysOffModule
                    ),
            },
            {
                path: 'work-tracker',
                canActivate: [AuthGuard, UserRolesGuard],
                data: {
                    allowedRoles: ['ROLE_super_admin', 'ROLE_admin', 'ROLE_user', 'ROLE_demo_user']
                },
                loadChildren: () =>
                    import('../modules/work-tracker/work-tracker.module').then(
                        (m) => m.WorkTrackerModule
                    ),
            },
            {
                path: 'feedback',
                canActivate: [AuthGuard],
                loadChildren: () =>
                    import('../modules/feedback/feedback.module').then(
                        (m) => m.FeedbackModule
                    ),
            },
            {
                path: 'reports-crm',
                canActivate: [AuthGuard, UserRolesGuard],
                data: {
                    allowedRoles: ['ROLE_super_admin', 'ROLE_admin', 'ROLE_CRMReportsFull', 'ROLE_CRMReportsView', 'ROLE_demo_user']
                },
                loadChildren: () =>
                    import('../modules/reports-crm/reports.module').then(
                        (m) => m.ReportsModule
                    ),
            },
            {
                path: 'reports-erp',
                canActivate: [AuthGuard, UserRolesGuard],
                data: {
                    allowedRoles: ['ROLE_super_admin', 'ROLE_admin', 'ROLE_ERPReportsFull', 'ROLE_ERPReportsView', 'ROLE_demo_user']
                },
                loadChildren: () =>
                    import('../modules/reports-erp/reports-erp.module').then(
                        (m) => m.ReportsErpModule
                    ),
            },
            {
                path: 'users',
                canActivate: [AuthGuard, UserRolesGuard],
                data: {
                    allowedRoles: ['ROLE_super_admin', 'ROLE_admin', 'ROLE_WorkTrackerFull', 'ROLE_LeaveRequestFull', 'ROLE_demo_user']
                },
                loadChildren: () =>
                    import('../modules/user-management/user-management.module').then(
                        (m) => m.UserManagementModule
                    ),
            },
            {
                path: 'user-profile',
                canActivate: [AuthGuard],
                loadChildren: () =>
                    import('../modules/user-profile/user-profile.module').then(
                        (m) => m.UserProfileModule
                    ),
            },
            {
                path: 'user-settings',
                canActivate: [AuthGuard, UserRolesGuard],
                data: {
                    allowedRoles: ['ROLE_super_admin', 'ROLE_admin']
                },
                loadChildren: () =>
                    import('../modules/user-settings/user-settings.module').then(
                        (m) => m.UserSettingsModule
                    ),
            },
            {
                path: 'offers',
                canActivate: [AuthGuard, UserRolesGuard],
                data: {
                    allowedRoles: ['ROLE_super_admin', 'ROLE_admin', 'ROLE_OffersFull', 'ROLE_OffersView', 'ROLE_demo_user']
                },
                loadChildren: () =>
                    import('../modules/offers/offers.module').then(
                        (m) => m.OffersModule
                    ),
            },
            {
                path: 'contracts',
                canActivate: [AuthGuard, UserRolesGuard],
                data: {
                    allowedRoles: ['ROLE_super_admin', 'ROLE_admin', 'ROLE_ContractsFull', 'ROLE_ContractsView', 'ROLE_demo_user']
                },
                loadChildren: () =>
                    import('../modules/contracts/contracts.module').then(
                        (m) => m.ContractsModule
                    ),
            },
            {
                path: 'contacts',
                canActivate: [AuthGuard, UserRolesGuard],
                data: {
                    allowedRoles: ['ROLE_super_admin', 'ROLE_admin', 'ROLE_ContactsFull', 'ROLE_ContactsView', 'ROLE_demo_user']
                },
                loadChildren: () =>
                    import('../modules/contacts/contacts.module').then(
                        (m) => m.ContactsModule
                    ),
            },
            {
                path: 'projects',
                canActivate: [AuthGuard, UserRolesGuard],
                data: {
                    allowedRoles: ['ROLE_super_admin', 'ROLE_admin', 'ROLE_ProjectsFull', 'ROLE_ProjectsView', 'ROLE_demo_user']
                },
                loadChildren: () =>
                    import('../modules/projects/projects.module').then(
                        (m) => m.ProjectsModule
                    ),
            },
            {
                path: 'tasks',
                canActivate: [AuthGuard, UserRolesGuard],
                data: {
                    allowedRoles: ['ROLE_super_admin', 'ROLE_admin', 'ROLE_ERPTasksFull', 'ROLE_ERPTasksView', 'ROLE_demo_user']
                },
                loadChildren: () =>
                    import('../modules/tasks/tasks.module').then(
                        (m) => m.TasksModule
                    ),
            },
            {
                path: 'tickets',
                canActivate: [AuthGuard, UserRolesGuard],
                data: {
                    allowedRoles: ['ROLE_super_admin', 'ROLE_admin', 'ROLE_client', 'ROLE_TicketsFull', 'ROLE_TicketsView', 'ROLE_demo_user']
                },
                loadChildren: () =>
                    import('../modules/tickets/tickets.module').then(
                        (m) => m.TicketsModule
                    ),
            },
            {
                path: 'procedures',
                canActivate: [AuthGuard, UserRolesGuard],
                data: {
                    allowedRoles: ['ROLE_super_admin', 'ROLE_admin', 'ROLE_ProceduresView', 'ROLE_ProceduresFull', 'ROLE_demo_user']
                },
                loadChildren: () => import('../modules/procedures/procedures.module').then(m => m.ProceduresModule)
            },
            {
                path: 'directives',
                canActivate: [AuthGuard, UserRolesGuard],
                data: {
                    allowedRoles: ['ROLE_super_admin', 'ROLE_admin', 'ROLE_DirectivesView', 'ROLE_DirectivesFull', 'ROLE_demo_user']
                },
                loadChildren: () => import('../modules/directives/directives.module').then(m => m.DirectivesModule)
            },
            {
                path: 'calendar',
                canActivate: [AuthGuard],
                loadChildren: () => import('../modules/calendar/calendar.module').then(m => m.FullCalendarModule)
            },
            {
                path: '',
                redirectTo: '/dashboard',
                pathMatch: 'full',
            },
            {
                path: '**',
                redirectTo: 'error/404',
            },
        ],
    },


];


@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
})
export class PagesRoutingModule {
}
